// "My Plan" display helpers + shared /subscriptions/my hook (display only — no gating).
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OD_MONTHS = ["ଜାନୁଆରୀ", "ଫେବୃଆରୀ", "ମାର୍ଚ୍ଚ", "ଅପ୍ରେଲ", "ମଇ", "ଜୁନ", "ଜୁଲାଇ", "ଅଗଷ୍ଟ", "ସେପ୍ଟେମ୍ବର", "ଅକ୍ଟୋବର", "ନଭେମ୍ବର", "ଡିସେମ୍ବର"];
const OD_DIGITS = "୦୧୨୩୪୫୬୭୮୯";
const odNum = (v) => String(v).replace(/\d/g, (d) => OD_DIGITS[d]);

const istParts = (iso) => {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })
    .formatToParts(d).map((x) => [x.type, x.value]));
  const m = Number(new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kolkata", month: "numeric" }).format(d)) - 1;
  return { ...p, m };
};
// "12 Nov 2026" (IST)
const EN_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const fmtDate = (iso) => { const p = istParts(iso); return p ? `${p.day} ${EN_MONTHS[p.m]} ${p.year}` : ""; };
// "୧୨ ନଭେମ୍ବର ୨୦୨୬"
export const fmtDateOd = (iso) => { const p = istParts(iso); return p ? `${odNum(p.day)} ${OD_MONTHS[p.m]} ${odNum(p.year)}` : ""; };
// "4:30 PM" (IST)
export const fmtTime = (iso) => { const p = istParts(iso); return p ? `${p.hour}:${p.minute} ${String(p.dayPeriod || "").toUpperCase()}` : ""; };
export const fmtDateTime = (iso) => (iso ? `${fmtDate(iso)}, ${fmtTime(iso)}` : "");
export const rupees = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function statusMeta(plan) {
  const s = plan?.status;
  if (s === "active") return { tone: "teal", label: "Active", od: "ସକ୍ରିୟ" };
  if (s === "expiring_soon") return { tone: "saffron", label: `Expiring in ${plan.daysLeft} day${plan.daysLeft === 1 ? "" : "s"}`, od: `${odNum(plan.daysLeft)} ଦିନରେ ଶେଷ` };
  if (s === "expired") return { tone: "coral", label: "Expired", od: "ମିଆଦ ଶେଷ" };
  if (s === "scheduled") return { tone: "grey", label: `Activating at ${fmtTime(plan.scheduledActivationAt)}`, od: "ଶୀଘ୍ର ସକ୍ରିୟ ହେବ" };
  return { tone: "grey", label: "No plan", od: "କୌଣସି ପ୍ଲାନ ନାହିଁ" };
}

export const TONE_CLASS = {
  teal: "bg-teal-100 text-teal-800 border-teal-300",
  saffron: "bg-amber-100 text-amber-800 border-amber-300",
  coral: "bg-rose-100 text-rose-700 border-rose-300",
  grey: "bg-slate-100 text-slate-700 border-slate-300",
};

export function sourceText(source, startedAt) {
  const on = startedAt ? ` on ${fmtDate(startedAt)}` : "";
  return ({
    payment: { en: `Paid online${on}`, od: "ଅନଲାଇନ ଦେୟ" },
    admin: { en: `Activated by admin${on}`, od: "ଆଡମିନ ଦ୍ୱାରା ସକ୍ରିୟ" },
    partner: { en: `Included with Teacher Utility ToolKit${on}`, od: "Teacher Utility ToolKit ସହ ସାମିଲ" },
    trial: { en: `Free trial${on}`, od: "ମାଗଣା ଟ୍ରାଏଲ" },
    auto_setup: { en: `Activated automatically after setup${on}`, od: "ସେଟଅପ ପରେ ଆପେ ସକ୍ରିୟ" },
  })[source] || { en: "Activation details not recorded", od: "ବିବରଣୀ ଉପଲବ୍ଧ ନାହିଁ" };
}

// Validity used, 0..1 (needs both dates).
export function usedFraction(plan) {
  const a = Date.parse(plan?.startedAt); const b = Date.parse(plan?.activeUntil);
  if (!a || !b || b <= a) return null;
  return Math.min(1, Math.max(0, (Date.now() - a) / (b - a)));
}

let cache = null; let cacheUid = null; let inflight = null; const subs = new Set();
export function refreshMyPlan(uid = cacheUid) {
  inflight = axios.get(`${API}/subscriptions/my`).then((r) => { cache = r.data; cacheUid = uid; subs.forEach((f) => f(cache)); return cache; })
    .catch(() => cache).finally(() => { inflight = null; });
  return inflight;
}
// uid = current school user's id (null → disabled, e.g. admin / logged out).
export function useMyPlan(uid) {
  const [plan, setPlan] = useState(uid && cacheUid === uid ? cache : null);
  useEffect(() => {
    if (!uid) return undefined;
    subs.add(setPlan);
    if (cacheUid !== uid) { cache = null; setPlan(null); if (!inflight) refreshMyPlan(uid); } else if (cache) setPlan(cache);
    return () => { subs.delete(setPlan); };
  }, [uid]);
  return plan;
}
export const clearMyPlanCache = () => { cache = null; };
